/**
 * Phase 8-G — Stats API client.
 *
 * Thin fetch wrapper matching the `billing-api.ts` / `admin-api.ts`
 * conventions. RTK Query is intentionally NOT used here for consistency
 * with the rest of the Phase 8 surface area.
 *
 * Endpoints (Phase 8-C backend):
 *   GET /api/v1/agency/stats?window=7d|30d|90d
 *       — owner/team_lead only. Scoped to the acting employee's org.
 *   GET /api/v1/admin/organisations/{id}/stats?window=7d|30d|90d
 *       — admin only. Same shape, for any org.
 *   GET /api/v1/admin/overview/stats?window=7d|30d|90d
 *       — admin only. Same shape, rolled up across all orgs.
 *
 * Auth header:
 *   - Agency: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 *   - Admin:  caller passes the admin token explicitly (matches
 *             `admin-api.ts` convention).
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

export type StatsWindow = '7d' | '30d' | '90d';

export interface StatsSummary {
  proposals_total: number;
  proposals_approved: number;
  proposals_denied: number;
  proposals_pending: number;
  proposals_auto_approved: number;
  /** 0.0 – 1.0 */
  auto_approval_rate: number;
  active_employees: number;
  active_shops: number;
}

export interface StatsPercentiles {
  p50: number | null;
  p90: number | null;
  p99: number | null;
}

export interface StatsPlatformBucket {
  platform: string;
  count: number;
}

export interface StatsToolBucket {
  tool_name: string;
  count: number;
}

export interface StatsExecutorHealthRow {
  platform: string;
  success: number;
  failure: number;
}

export interface StatsShopBucket {
  shop_slug: string;
  shop_name: string;
  count: number;
}

/**
 * Optional risk-level histogram. Backend may not emit this on every window
 * (Phase 8-C ships it best-effort). Keys map to a 4-bucket low/medium/high/critical
 * scale. Missing keys are treated as 0.
 */
export interface StatsRiskDistribution {
  low?: number;
  medium?: number;
  high?: number;
  critical?: number;
  [key: string]: number | undefined;
}

export interface StatsResponse {
  window: StatsWindow;
  window_start: string; // ISO8601
  window_end: string; // ISO8601
  summary: StatsSummary;
  time_to_approve_ms: StatsPercentiles;
  proposals_by_platform: StatsPlatformBucket[];
  /** Top 10. */
  proposals_by_tool: StatsToolBucket[];
  executor_success_rate: StatsExecutorHealthRow[];
  slack_responsiveness_ms: StatsPercentiles;
  /** Top 10. */
  approved_per_shop: StatsShopBucket[];
  /** Optional per Phase 8-C; omitted when backend has no risk data. */
  risk_distribution?: StatsRiskDistribution;
}

// ---------- internal helpers ----------

function buildHeaders(token: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[stats-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  token: string | undefined,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    ...buildHeaders(token),
    ...((init.headers as Record<string, string>) || {}),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function buildWindowQuery(window: StatsWindow): string {
  const params = new URLSearchParams();
  params.set('window', window);
  return params.toString();
}

// ---------- public API ----------

/**
 * Fetch agency-scoped stats for the acting employee's organisation.
 * Backend enforces owner/team_lead role.
 */
export async function getAgencyStats(
  window: StatsWindow,
  token?: string
): Promise<StatsResponse> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const raw = await request<StatsResponse | { data?: StatsResponse }>(
    `agency/stats?${buildWindowQuery(window)}`,
    t,
    { method: 'GET' }
  );
  return unwrap<StatsResponse>(raw);
}

/**
 * Fetch admin-scoped stats for a single organisation.
 */
export async function getAdminOrganisationStats(
  orgId: string,
  window: StatsWindow,
  adminToken?: string
): Promise<StatsResponse> {
  const t = adminToken ?? store.getState().auth.admin.accessToken;
  const raw = await request<StatsResponse | { data?: StatsResponse }>(
    `admin/organisations/${encodeURIComponent(orgId)}/stats?${buildWindowQuery(
      window
    )}`,
    t,
    { method: 'GET' }
  );
  return unwrap<StatsResponse>(raw);
}

/**
 * Fetch admin-scoped stats rolled up across all organisations.
 */
export async function getAdminOverviewStats(
  window: StatsWindow,
  adminToken?: string
): Promise<StatsResponse> {
  const t = adminToken ?? store.getState().auth.admin.accessToken;
  const raw = await request<StatsResponse | { data?: StatsResponse }>(
    `admin/overview/stats?${buildWindowQuery(window)}`,
    t,
    { method: 'GET' }
  );
  return unwrap<StatsResponse>(raw);
}
